import { useState, useEffect } from 'react';
import { apiRequest, getImageUrl } from '../../utils/api';

const RestaurantFormModal = ({ isOpen, onClose, restaurant, organizationId, onSuccess }) => {
    const [formData, setFormData] = useState({
        name: '',
        description: '',
        phone: '',
        email: '',
        address: '',
        website: '',
        facebook_url: '',
        instagram_url: '',
        twitter_url: '',
        whatsapp_number: '',
        status: 'active'
    });
    const [logo, setLogo] = useState(null);
    const [logoPreview, setLogoPreview] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    useEffect(() => {
        if (restaurant) {
            setFormData({
                name: restaurant.name || '',
                description: restaurant.description || '',
                phone: restaurant.phone || '',
                email: restaurant.email || '',
                address: restaurant.address || '',
                website: restaurant.website || '',
                facebook_url: restaurant.facebook_url || '',
                instagram_url: restaurant.instagram_url || '',
                twitter_url: restaurant.twitter_url || '',
                whatsapp_number: restaurant.whatsapp_number || '',
                status: restaurant.status || 'active'
            });
            setLogoPreview(restaurant.logo || null);
        } else {
            resetForm();
        }
    }, [restaurant, isOpen]);

    const resetForm = () => {
        setFormData({
            name: '',
            description: '',
            phone: '',
            email: '',
            address: '',
            website: '',
            facebook_url: '',
            instagram_url: '',
            twitter_url: '',
            whatsapp_number: '',
            status: 'active'
        });
        setLogo(null);
        setLogoPreview(null);
        setError(null);
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handleLogoChange = (e) => {
        const file = e.target.files[0];
        if (!file) return;

        // Validate file type
        const validTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/webp'];
        if (!validTypes.includes(file.type)) {
            setError('Geçersiz dosya formatı. Sadece JPG, PNG, GIF veya WebP formatları desteklenir.');
            e.target.value = ''; // Reset input
            return;
        }

        // Validate file size (max 5MB)
        const maxSize = 5 * 1024 * 1024; // 5MB
        if (file.size > maxSize) {
            setError('Dosya boyutu çok büyük. Maksimum 5MB olmalıdır.');
            e.target.value = ''; // Reset input
            return;
        }

        setError(null);
        setLogo(file);
        const reader = new FileReader();
        reader.onerror = () => {
            setError('Dosya okunurken bir hata oluştu.');
        };
        reader.onloadend = () => {
            setLogoPreview(reader.result);
        };
        reader.readAsDataURL(file);
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const formDataToSend = new FormData();
            Object.keys(formData).forEach(key => {
                if (formData[key]) {
                    formDataToSend.append(key, formData[key]);
                }
            });
            if (logo) {
                formDataToSend.append('logo', logo);
            }

            const url = restaurant
                ? `/organizations/${organizationId}/restaurants/${restaurant.id}`
                : `/organizations/${organizationId}/restaurants`;

            const response = await apiRequest(url, {
                method: restaurant ? 'PUT' : 'POST',
                headers: {
                    // Don't set Content-Type for FormData, browser will set it with boundary
                },
                body: formDataToSend
            });

            onSuccess();
            onClose();
            resetForm();
        } catch (err) {
            setError(err.message || 'Restoran kaydedilirken bir hata oluştu');
        } finally {
            setLoading(false);
        }
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
            <div className="bg-white rounded-lg max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                <div className="p-6">
                    <div className="flex items-center justify-between mb-6">
                        <h2 className="text-2xl font-bold text-gray-900">
                            {restaurant ? 'Restoran Düzenle' : 'Yeni Restoran'}
                        </h2>
                        <button
                            onClick={onClose}
                            className="text-gray-400 hover:text-gray-600"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>

                    {error && (
                        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                            {error}
                        </div>
                    )}

                    <form onSubmit={handleSubmit} className="space-y-4">
                        {/* Logo */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-2">
                                Logo
                            </label>
                            <div className="flex items-center space-x-4">
                                {logoPreview && (
                                    <div className="relative">
                                        <img
                                            src={logoPreview.startsWith('blob:') || logoPreview.startsWith('data:') ? logoPreview : getImageUrl(logoPreview)}
                                            alt="Logo preview"
                                            className="w-24 h-24 object-cover rounded-lg"
                                            onError={(e) => {
                                                console.error('Logo preview error:', e);
                                                setError('Logo önizlemesi yüklenemedi.');
                                            }}
                                        />
                                        <button
                                            type="button"
                                            onClick={() => {
                                                setLogo(null);
                                                setLogoPreview(null);
                                                const input = document.querySelector('input[type="file"]');
                                                if (input) input.value = '';
                                            }}
                                            className="absolute top-0 right-0 bg-red-500 text-white rounded-full w-5 h-5 flex items-center justify-center text-xs hover:bg-red-600 -mt-1 -mr-1"
                                        >
                                            ×
                                        </button>
                                    </div>
                                )}
                                <input
                                    type="file"
                                    accept="image/*"
                                    onChange={handleLogoChange}
                                    className="block w-full text-sm text-gray-500 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-semibold file:bg-blue-50 file:text-blue-700 hover:file:bg-blue-100"
                                />
                            </div>
                        </div>

                        {/* Name */}
                        <div>
                            <label htmlFor="name" className="block text-sm font-medium text-gray-700 mb-2">
                                Restoran Adı *
                            </label>
                            <input
                                type="text"
                                id="name"
                                name="name"
                                required
                                value={formData.name}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Description */}
                        <div>
                            <label htmlFor="description" className="block text-sm font-medium text-gray-700 mb-2">
                                Açıklama
                            </label>
                            <textarea
                                id="description"
                                name="description"
                                rows="3"
                                value={formData.description}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Contact Info */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="phone" className="block text-sm font-medium text-gray-700 mb-2">
                                    Telefon
                                </label>
                                <input
                                    type="tel"
                                    id="phone"
                                    name="phone"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                                    E-posta
                                </label>
                                <input
                                    type="email"
                                    id="email"
                                    name="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        {/* Address */}
                        <div>
                            <label htmlFor="address" className="block text-sm font-medium text-gray-700 mb-2">
                                Adres
                            </label>
                            <textarea
                                id="address"
                                name="address"
                                rows="2"
                                value={formData.address}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Website */}
                        <div>
                            <label htmlFor="website" className="block text-sm font-medium text-gray-700 mb-2">
                                Web Sitesi
                            </label>
                            <input
                                type="url"
                                id="website"
                                name="website"
                                value={formData.website}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            />
                        </div>

                        {/* Social Media */}
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                            <div>
                                <label htmlFor="facebook_url" className="block text-sm font-medium text-gray-700 mb-2">
                                    Facebook URL
                                </label>
                                <input
                                    type="url"
                                    id="facebook_url"
                                    name="facebook_url"
                                    value={formData.facebook_url}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label htmlFor="instagram_url" className="block text-sm font-medium text-gray-700 mb-2">
                                    Instagram URL
                                </label>
                                <input
                                    type="url"
                                    id="instagram_url"
                                    name="instagram_url"
                                    value={formData.instagram_url}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label htmlFor="twitter_url" className="block text-sm font-medium text-gray-700 mb-2">
                                    Twitter URL
                                </label>
                                <input
                                    type="url"
                                    id="twitter_url"
                                    name="twitter_url"
                                    value={formData.twitter_url}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                            <div>
                                <label htmlFor="whatsapp_number" className="block text-sm font-medium text-gray-700 mb-2">
                                    WhatsApp Numarası
                                </label>
                                <input
                                    type="tel"
                                    id="whatsapp_number"
                                    name="whatsapp_number"
                                    value={formData.whatsapp_number}
                                    onChange={handleChange}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                                />
                            </div>
                        </div>

                        {/* Status */}
                        <div>
                            <label htmlFor="status" className="block text-sm font-medium text-gray-700 mb-2">
                                Durum
                            </label>
                            <select
                                id="status"
                                name="status"
                                value={formData.status}
                                onChange={handleChange}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                            >
                                <option value="active">Aktif</option>
                                <option value="inactive">Pasif</option>
                            </select>
                        </div>

                        {/* Actions */}
                        <div className="flex space-x-3 pt-4">
                            <button
                                type="button"
                                onClick={onClose}
                                className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition"
                            >
                                İptal
                            </button>
                            <button
                                type="submit"
                                disabled={loading}
                                className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition"
                            >
                                {loading ? 'Kaydediliyor...' : 'Kaydet'}
                            </button>
                        </div>
                    </form>
                </div>
            </div>
        </div>
    );
};

export default RestaurantFormModal;

